import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./Match.module.css";
import MatchesAPI from "../../data/matches";
import Subscription from "../Subscription/Subscription";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaHome,
  FaUserFriends,
  FaTrophy,
  FaFilter,
  FaAward,
  FaInfoCircle,
  FaTimesCircle,
  FaCalendar,
  FaArrowRight,
} from "react-icons/fa";
import { MdSportsCricket } from "react-icons/md";
import { GlobalContext } from "../../constants/Context";
import { useMatchesState } from "../../states/useMatchState";
import CustomDropdown from "../../components/Input/CustomDropdown";
const Matches = () => {
  const navigate = useNavigate();

  const { SITE_ID, CLUD_ID } = useContext(GlobalContext);

  const LEAGUE_ID = SITE_ID;
  const {
    matches,
    setMatches,
    errors,
    setErrors,
    loading,
    setLoading,
    filter,
    setFilter,
    competitions,
    setCompetitions,
    selectedCompetitionId,
    setSelectedCompetitionId,
    yearsList,
    setYearsList,
    selectedYear,
    setSelectedYear,
    currentLiveMatchIndex,
    setCurrentLiveMatchIndex,
    groupedMatches,
    setGroupedMatches,
    liveMatches,
    setLiveMatches,
    apiKey,
    setApiKey,
  } = useMatchesState();
  // Generate years list (from 2000 to current year + 5) - LeagueList style
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const startYear = 2000;
    const endYear = currentYear + 5;

    const years = [];
    for (let year = endYear; year >= startYear; year--) {
      years.push({
        value: year.toString(),
        label: year.toString(),
      });
    }
    setYearsList(years);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Ref to track if data has been loaded
  const matchesDataLoadedRef = useRef(false);
  // Key to force re-fetch when season changes

  // Function to determine if match is live based on date, time and API status
  const isMatchLive = useCallback((matchDate, matchTime, originalStatus) => {
    if (!matchDate || matchDate === "Undecided") return false;

    // Check original status from API first
    const status = (originalStatus || "").toLowerCase();
    if (status.includes("live") || status.includes("inprogress") || status.includes("started")) {
      return true;
    }

    try {
      // Parse match date (DD/MM/YYYY format)
      const [day, month, year] = matchDate.split('/');
      const matchDateTime = new Date(year, month - 1, day);

      // Parse match time if available
      if (matchTime && matchTime.includes(':')) {
        const [hours, minutes] = matchTime.split(':');
        matchDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      } else {
        // Default to 12:00 PM if no time specified
        matchDateTime.setHours(12, 0, 0, 0);
      }

      // Get current date and time
      const now = new Date();

      // Match is considered live if:
      // 1. Match date is today AND
      // 2. Current time is within 8 hours of match start time
      const isToday = matchDateTime.toDateString() === now.toDateString();

      if (isToday) {
        const timeDiff = Math.abs(now - matchDateTime) / (1000 * 60 * 60); // Difference in hours
        return timeDiff <= 8; // Match is "live" for 8 hours from start time
      }

      return false;
    } catch (error) {
      console.error("Error checking if match is live:", error);
      return false;
    }
  }, []);

  // Function to get match status with live detection
  const determineMatchStatus = useCallback((matchDate, originalStatus, matchTime) => {
    const matchStatus = (originalStatus || "").toLowerCase();

    // First check if match is cancelled/abandoned
    if (matchStatus.includes("abandoned") || matchStatus.includes("cancelled")) {
      return "cancelled";
    }

    // Check if match is live based on date and time
    if (isMatchLive(matchDate, matchTime, originalStatus)) {
      return "live";
    }

    // Check if match is completed based on status
    if (matchStatus.includes("completed") || matchStatus.includes("result")) {
      return "completed";
    }

    // Parse the match date (DD/MM/YYYY format)
    try {
      const [day, month, year] = matchDate.split('/');
      const matchDateObj = new Date(year, month - 1, day);
      const today = new Date();

      // Set both dates to midnight for accurate comparison
      matchDateObj.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Compare dates
      if (matchDateObj < today) {
        return "completed"; // Match date is in the past
      } else if (matchDateObj > today) {
        return "upcoming"; // Match date is in the future
      } else {
        // If it's today but not within live time window
        return "upcoming"; // Will be live when time comes
      }
    } catch (error) {
      console.error("Error parsing date:", matchDate, error);
      // If date parsing fails, check if it's a past season
      if (selectedYear < new Date().getFullYear().toString()) {
        return "completed"; // Past season matches are completed
      }
      return "upcoming";
    }
  }, [isMatchLive, selectedYear]);

  // Format match date for display
  const formatMatchDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const [day, month, year] = dateString.split('/');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day} ${months[parseInt(month) - 1]} ${year}`;
    } catch (err) {
      return dateString;
    }
  };

  // Get full formatted date for grouping - Same as leagueTable.jsx
  const getFullFormattedDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    try {
      const [day, month, year] = dateString.split('/');
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateString;
    }
  };

  // Function to identify live matches from all matches
  const identifyLiveMatches = useCallback((matchesList) => {
    // First, filter matches that are actually live based on status
    const liveMatchesList = matchesList.filter(match => {
      return match.status === "live";
    });

    // Only include matches that have actual score data from API
    const liveMatchesData = liveMatchesList.map(match => {
      const formatMatchDate = (dateString) => {
        if (!dateString) return "";
        try {
          const [day, month, year] = dateString.split('/');
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric"
          });
        } catch (e) {
          return dateString;
        }
      };

      // Check if we have actual score data from API
      const hasScoreData = match.score_team1 && match.score_team1 !== "0/0";

      // If no score data, still show basic match info but without scores
      if (!hasScoreData) {
        return {
          id: match.id,
          matchId: match.id,
          status: "Live",
          leagueName: match.league?.name,
          competitionName: match.competition?.name,
          matchDate: formatMatchDate(match.original_date),
          matchTime: match.match_time || "12:00",
          groundName: match.venue || "TBD",
          noOfOvers: match.no_of_overs,
          matchType: match.match_type,

          // Teams
          homeTeam: match.team1_data,
          awayTeam: match.team2_data,

          // No score data available
          hasScoreData: false,

          // Additional info
          lastUpdated: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      }

      // Parse scores from API data if available
      const parseScore = (scoreString) => {
        if (!scoreString || scoreString === "0/0") return null;
        const [runs, wickets] = scoreString.split('/').map(num => parseInt(num) || 0);
        return { runs, wickets };
      };

      const team1Score = parseScore(match.score_team1);
      const team2Score = parseScore(match.score_team2);

      // Determine batting and bowling teams
      let battingTeam = match.team1_data;
      let bowlingTeam = match.team2_data;
      let target = "N/A";
      let requiredRunRate = "N/A";
      let currentInnings = null;

      if (team1Score) {
        currentInnings = {
          battingTeamId: battingTeam.id,
          runs: team1Score.runs,
          wickets: team1Score.wickets,
          overs: match.current_over || "0.0",
          extras: 0,
          totalBalls: parseFloat(match.current_over || "0") * 6
        };
      }

      return {
        id: match.id,
        matchId: match.id,
        status: "Live",
        leagueName: match.league?.name,
        competitionName: match.competition?.name,
        matchDate: formatMatchDate(match.original_date),
        matchTime: match.match_time || "12:00",
        groundName: match.venue || "TBD",
        noOfOvers: match.no_of_overs,
        matchType: match.match_type,

        // Teams
        homeTeam: match.team1_data,
        awayTeam: match.team2_data,

        // Live score data - only if available
        currentInnings: currentInnings,

        // Match statistics
        target: target,
        requiredRunRate: requiredRunRate,
        battingTeam: battingTeam,
        bowlingTeam: bowlingTeam,
        lastUpdated: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),

        // Flag for score data availability
        hasScoreData: true,

        // Additional info
        toss: match.toss_info || null
      };
    });

    return liveMatchesData;
  }, []);

  // Group matches by date - MODIFIED: Sort dates chronologically (oldest first)
  const groupMatchesByDate = useCallback((matchesList) => {
    const grouped = {};

    matchesList.forEach(match => {
      const dateStr = getFullFormattedDate(match.match_date);
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push(match);
    });

    // Sort dates chronologically - OLDEST FIRST (past matches first)
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => {
        try {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA - dateB; // Changed: Oldest first (dateA - dateB)
        } catch (err) {
          return a.localeCompare(b);
        }
      })
      .forEach(date => {
        // Sort matches within each date by time
        grouped[date].sort((a, b) => {
          const timeA = a.match_time || "23:59";
          const timeB = b.match_time || "23:59";
          return timeA.localeCompare(timeB);
        });
        sortedGrouped[date] = grouped[date];
      });

    return sortedGrouped;
  }, []);

  // Handle matches data from API component
  const handleMatchesData = useCallback((data) => {

    if (!data || data.length === 0) {
      setErrors(`No matches found for ${selectedYear} season`);
      setMatches([]);
      setGroupedMatches({});
      setLoading(false);
      return;
    }

    // Filter matches by league_id (site_id)
    const leagueMatches = data.filter(match =>
      match.league_id && match.league_id.toString() === LEAGUE_ID.toString()
    );

    // Transform matches data
    const transformedMatches = leagueMatches.map(match => {
      // Use the determineMatchStatus function to get correct status based on date
      const status = determineMatchStatus(match.match_date, match.status, match.match_time);

      // Format match date from DD/MM/YYYY to more readable format
      const formatMatchDate = (dateString) => {
        if (!dateString) return "Undecided";

        try {
          const [day, month, year] = dateString.split('/');
          return `${day}/${month}/${year}`;
        } catch (e) {
          return dateString;
        }
      };

      // Get team short names
      const getTeamShortName = (teamName) => {
        if (!teamName) return "Undecided";
        const words = teamName.split(' ');
        if (words.length > 1) {
          return words.map(word => word.charAt(0)).join('').toUpperCase();
        }
        return teamName.substring(0, 3).toUpperCase();
      };

      // Extract team name from club + team name
      const getFullTeamName = (clubName, teamName) => {
        if (!clubName) return teamName || "Unknown Team";
        if (!teamName) return clubName;
        return `${clubName} ${teamName}`;
      };

      // Check if API has actual score data
      const hasAPIScore = match.score_team1 && match.score_team1 !== "0/0";

      return {
        id: match.id,
        match_date: formatMatchDate(match.match_date),
        original_date: match.match_date, // Keep original format for filtering
        match_time: match.match_time || "12:00",
        status: status,
        venue: match.ground_name || "Undecided",
        team1_data: {
          id: match.home_team_id,
          name: getFullTeamName(match.home_club_name, match.home_team_name),
          short_name: getTeamShortName(match.home_club_name || match.home_team_name),
          logo: "/default-team.png",
          club_name: match.home_club_name,
          team_name: match.home_team_name
        },
        team2_data: {
          id: match.away_team_id,
          name: getFullTeamName(match.away_club_name, match.away_team_name),
          short_name: getTeamShortName(match.away_club_name || match.away_team_name),
          logo: "/default-team.png",
          club_name: match.away_club_name,
          team_name: match.away_team_name
        },
        // Only use score from API if available, otherwise show "0/0"
        score_team1: hasAPIScore ? match.score_team1 : "0/0",
        score_team2: hasAPIScore ? match.score_team2 : "0/0",
        current_over: hasAPIScore ? (match.current_over || "0.0") : "0.0",
        current_run_rate: hasAPIScore ? (match.current_run_rate || "0.00") : "0.00",
        league: {
          id: match.league_id || LEAGUE_ID,
          name: match.league_name
        },
        competition: {
          id: match.competition_id,
          name: match.competition_name
        },
        match_type: match.match_type,
        game_type: match.game_type,
        umpire_1: match.umpire_1_name,
        umpire_2: match.umpire_2_name,
        is_featured: status === "live",
        original_status: match.status, // Keep original status from API
        has_score_data: hasAPIScore // Flag for score data availability
      };
    });

    // Extract unique competitions for filter dropdown
    const uniqueCompetitions = [];
    const compIds = new Set();

    leagueMatches.forEach(match => {
      if (match.competition_id && !compIds.has(match.competition_id)) {
        compIds.add(match.competition_id);
        uniqueCompetitions.push({
          id: match.competition_id,
          name: match.competition_name
        });
      }
    });

    setCompetitions(uniqueCompetitions);
    setMatches(transformedMatches);

    // Group matches by date
    const grouped = groupMatchesByDate(transformedMatches);
    setGroupedMatches(grouped);

    // Identify live matches (only those with live status)
    const liveMatchesData = identifyLiveMatches(transformedMatches);
    setLiveMatches(liveMatchesData);

    setErrors(null);
    setLoading(false);

    // Log status distribution for debugging
    const statusCount = {
      completed: transformedMatches.filter(m => m.status === "completed").length,
      upcoming: transformedMatches.filter(m => m.status === "upcoming").length,
      live: transformedMatches.filter(m => m.status === "live").length,
      cancelled: transformedMatches.filter(m => m.status === "cancelled").length
    };
  }, [determineMatchStatus, identifyLiveMatches, groupMatchesByDate, selectedYear]);

  // Handle API error
  const handleAPIError = useCallback((errorMsg) => {
    console.error("Matches API Error:", errorMsg);
    setErrors(`Failed to load matches: ${errorMsg}`);
    setLoading(false);
  }, []);

  // Handle API loading
  const handleAPILoading = useCallback((isLoading) => {
    // We handle loading state ourselves
  }, []);

  // Filter matches by competition and status
  const filterAndGroupMatches = useCallback(() => {
    if (!matches.length) return {};

    // Filter by competition
    const filteredByCompetition = selectedCompetitionId === "all"
      ? matches
      : matches.filter(match =>
        match.competition.id && match.competition.id.toString() === selectedCompetitionId.toString()
      );

    // Further filter by status
    const filteredByStatus = filter === "all"
      ? filteredByCompetition
      : filteredByCompetition.filter(match => match.status === filter);

    // Group the filtered matches by date
    return groupMatchesByDate(filteredByStatus);
  }, [matches, selectedCompetitionId, filter, groupMatchesByDate]);

  // Update grouped matches when filters change
  useEffect(() => {
    if (matches.length > 0) {
      const filteredGrouped = filterAndGroupMatches();
      setGroupedMatches(filteredGrouped);
    }
  }, [matches, selectedCompetitionId, filter, filterAndGroupMatches]);

  // Format match date and time for display
  const formatMatchDateTime = useCallback((dateString, timeString) => {
    if (!dateString || dateString === "Undecided") return {
      date: "Undecided",
      time: "Undecided",
      day: "Undecided",
      fullDateTime: "Undecided"
    };

    try {
      // Parse DD/MM/YYYY format
      const [day, month, year] = dateString.split('/');
      const date = new Date(year, month - 1, day);

      let displayTime = "12:00";
      if (timeString && timeString.includes(':')) {
        displayTime = timeString;
      }

      const formattedDate = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      const formattedDay = date.toLocaleDateString("en-US", {
        weekday: "short"
      });

      return {
        date: formattedDate,
        time: displayTime,
        day: formattedDay,
        fullDateTime: `${formattedDate} • ${displayTime}`
      };
    } catch (error) {
      return {
        date: dateString,
        time: timeString || "Undecided",
        day: "Undecided",
        fullDateTime: dateString
      };
    }
  }, []);

  const getMatchStatus = useCallback((match) => {
    if (match.status === "live") return "Live";
    if (match.status === "completed") return "Completed";
    if (match.status === "cancelled") return "Cancelled";
    return "Upcoming";
  }, []);

  const getStatusClass = useCallback((status) => {
    switch (status) {
      case "live":
        return styles.live;
      case "completed":
        return styles.completed;
      case "cancelled":
        return styles.cancelled;
      default:
        return styles.upcoming;
    }
  }, []);

  const nextLiveMatch = () => {
    setCurrentLiveMatchIndex((prevIndex) =>
      prevIndex === liveMatches.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevLiveMatch = () => {
    setCurrentLiveMatchIndex((prevIndex) =>
      prevIndex === 0 ? liveMatches.length - 1 : prevIndex - 1
    );
  };

  // Get match statistics
  const getMatchStats = useCallback(() => {
    return {
      total: matches.length,
      upcoming: matches.filter(m => m.status === "upcoming").length,
      live: matches.filter(m => m.status === "live").length,
      completed: matches.filter(m => m.status === "completed").length,
      cancelled: matches.filter(m => m.status === "cancelled").length
    };
  }, [matches]);

  const matchStats = getMatchStats();

  // Get selected competition name
  const selectedCompetition = selectedCompetitionId === "all"
    ? { name: "All Competitions" }
    : competitions.find(comp => comp.id.toString() === selectedCompetitionId);

  // Handle year change
  const handleYearChange = (year) => {
    if (!year || year === selectedYear) return;

    setSelectedYear(year);
    // Reset states for new season
    setMatches([]);
    setGroupedMatches({});
    setLiveMatches([]);
    setCompetitions([]);
    setSelectedCompetitionId("all");
    setFilter("all");
    setCurrentLiveMatchIndex(0);
    setLoading(true);
    setErrors(null);

    // Force re-fetch of data for new season by changing API key
    setApiKey(prevKey => prevKey + 1);
  };

  // Render a single match card - Updated to match leagueTable.jsx style
  const renderMatchCard = (match, index) => {
    const formattedDateTime = formatMatchDateTime(match.match_date, match.match_time);
    const matchStatus = getMatchStatus(match);
    const statusClass = getStatusClass(match.status);

    return (
      <div key={match.id} className={`${styles.matchCard} ${styles.upcomingMatch}`}>
        <div className={styles.matchHeader}>
          <div className={styles.matchDateContainer}>
            <FaCalendarAlt />
            <span className={styles.matchDate}>
              {formatMatchDate(match.match_date)}
            </span>
            {formattedDateTime.time !== "Undecided" && (
              <span className={styles.matchTime}>
                | {formattedDateTime.time}
              </span>
            )}
          </div>
          <div className={styles.matchFormat}>
            <FaClock />
            <span>{match.match_type}</span>
          </div>
        </div>

        {match.venue && match.venue !== "Add New Ground" && (
          <div className={styles.venueInfo}>
            <FaMapMarkerAlt />
            <span>{match.venue}</span>
          </div>
        )}

        <div className={styles.teamsContainer}>
          {/* Home Team */}
          <div className={styles.teamRow}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {match.team1_data?.name}
                </span>
                <span className={styles.homeBadge}>
                  <FaHome />
                </span>
              </div>
              <span className={styles.teamRole}>
                Home Team
              </span>
            </div>
            {match.status === "completed" && match.score_team1 && match.score_team1 !== "0/0" && (
              <div className={styles.scoreInfo}>
                <span className={styles.score}>
                  {match.score_team1}
                </span>
                {match.current_over && match.current_over !== "0.0" && (
                  <span className={styles.overs}>
                    ({match.current_over} ov)
                  </span>
                )}
              </div>
            )}
          </div>
          <div className={styles.vsSeparator}>
            <span className={styles.vsSpan}>VS</span>
          </div>

          {/* Away Team */}
          <div className={styles.teamRow}>
            <div className={styles.teamInfo}>
              <div className={styles.teamNameWrapper}>
                <span className={styles.teamName}>
                  {match.team2_data?.name}
                </span>
                <span className={styles.awayBadge}>
                  <FaUserFriends />
                </span>
              </div>
              <span className={styles.teamRole}>
                Away Team
              </span>
            </div>
            {match.status === "completed" && match.score_team2 && match.score_team2 !== "0/0" && (
              <div className={styles.scoreInfo}>
                <span className={styles.score}>
                  {match.score_team2}
                </span>
                {match.current_run_rate && match.current_run_rate !== "0.00" && (
                  <span className={styles.runRate}>
                    RR: {match.current_run_rate}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles.matchFooter}>
          <div className={styles.matchDetails} style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <div className={styles.competitionInfo}>
              <FaTrophy />
              <span>{match.competition?.name}</span>
            </div>
            {match.status === "completed" && (
              <div className={styles.matchActions}>
                <Link
                  to={`/cricket-match/${match.id}`}
                  className={styles.matchButton}
                >
                  View Result <FaArrowRight />
                </Link>
              </div>
            )}
            <div className={`${styles.matchStatusBadge} ${statusClass}`}>
              <span>
                {match.status === "live" ? "Live Now" :
                  match.status === "completed" ? "Completed" :
                    match.status === "cancelled" ? "Cancelled" : "Upcoming"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render grouped matches by date - Same as leagueTable.jsx
  const renderGroupedMatches = () => {
    const dates = Object.keys(groupedMatches);

    if (dates.length === 0) {
      return (
        <div className={styles.noMatches}>
          <div className={styles.noMatchesIcon}>
            {filter === "live" && <MdSportsCricket/>}
            {filter === "completed" && <FaTrophy />}
            {filter === "upcoming" && <FaCalendar />}
            {filter === "cancelled" && <FaTimesCircle />}
            {filter === "all" && <FaTrophy />}
          </div>

          <h3>No {filter !== "all" ? filter : ""} matches found</h3>
          <p>Try selecting a different season or filter</p>
          
        </div>
      );
    }

    return (
      <div className={styles.groupedMatches}>
        {dates.map(date => (
          <div key={date} className={styles.dateCategory}>
            <div className={styles.categoryHeader}>
              <div className={styles.categoryTitle}>
                <span className={styles.categoryIcon}>
                  <FaCalendarAlt />
                </span>
                <h4 className={styles.categoryName}>
                  {date}
                </h4>
              </div>
            </div>
            <div className={styles.categoryMatches}>
              {groupedMatches[date].map((match, index) => renderMatchCard(match, index))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const yearOptions = useMemo(() =>
    yearsList?.map(year => ({
      value: year.value,
      label: year.label
    })) || [], [yearsList]
  );
  const competitionOptions = useMemo(() =>
    competitions?.map(comp => ({
      value: comp.id.toString(),
      label: comp.name
    })) || [], [competitions]
  )
  // Live Score Section Component - Only show if there are live matches
  const LiveScoreSection = () => {
    // Check if there are live matches
    if (!liveMatches || liveMatches.length === 0) {
      return null; // Don't render anything if no live matches
    }

    const currentMatch = liveMatches[currentLiveMatchIndex];

    // Check if currentMatch exists
    if (!currentMatch) {
      return null;
    }

    return (
      <section className={styles.liveScoreSection}>
        <div className={styles.container}>
          <div className={styles.liveScoreHeader}>
            <div className={styles.liveScoreTitle}>
              <span className={styles.liveDot}></span>
              Live Match
            </div>
            <div className={styles.liveMatchesCount}>
              Match {currentLiveMatchIndex + 1} of {liveMatches.length}
            </div>
          </div>

          <div className={styles.liveScoreContainer}>
            {/* Left Navigation Button */}
            {liveMatches.length > 1 && (
              <button
                className={`${styles.liveScoreNav} ${styles.prev}`}
                onClick={prevLiveMatch}
                aria-label="Previous live match"
              >
                <FaChevronLeft />
              </button>
            )}

            {/* Live Score Card */}
            <div className={styles.liveScoreCard}>
              {/* Match Header */}
              <div className={styles.liveMatchHeader}>
                <div className={styles.liveMatchInfo}>
                  <h3 className={styles.liveMatchTitle}>
                    {currentMatch.competitionName} • {currentMatch.matchType}
                  </h3>
                  <div className={styles.liveMatchMeta}>
                    <span><FaCalendarAlt /> {currentMatch.matchDate || "Date TBD"}</span>
                    <span><FaClock /> {currentMatch.matchTime || "Time TBD"}</span>
                    <span><FaMapMarkerAlt /> {currentMatch.groundName || "Venue TBD"}</span>
                  </div>
                </div>
                <div className={styles.liveMatchStatus}>
                  LIVE
                </div>
              </div>

              {/* Teams and Score */}
              <div className={styles.liveScoreTeams}>
                {/* Team 1 */}
                <div className={styles.liveTeam}>
                  <div className={styles.liveTeamHeader}>
                    <div className={styles.liveTeamName}>
                      <h3>{currentMatch.homeTeam?.name || 'TBD'}</h3>
                      <div className={styles.liveTeamClub}>
                        {currentMatch.homeTeam?.club_name || ''}
                      </div>
                    </div>
                    {currentMatch.hasScoreData && currentMatch.currentInnings && (
                      <div className={styles.liveTeamScore}>
                        <span className={styles.liveTeamRuns}>
                          {currentMatch.currentInnings?.runs || 0}
                        </span>
                        <span className={styles.liveTeamWickets}>
                          /{currentMatch.currentInnings?.wickets || 0}
                        </span>
                        <div className={styles.liveTeamOvers}>
                          ({currentMatch.currentInnings?.overs || 0} ov)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Score Details if available */}
                  {currentMatch.hasScoreData && currentMatch.currentInnings && (
                    <div className={styles.liveScoreDetails}>
                      <div className={styles.liveInningsInfo}>
                        <div className={styles.liveInningsItem}>
                          <span className={styles.liveInningsLabel}>Current Run Rate</span>
                          <span className={styles.liveInningsValue}>
                            {currentMatch.currentInnings.runs && currentMatch.currentInnings.overs
                              ? (parseFloat(currentMatch.currentInnings.runs) / parseFloat(currentMatch.currentInnings.overs)).toFixed(2)
                              : '0.00'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* VS Separator */}
                <div className={styles.liveVSSeparator}>
                  <div className={styles.liveVS}>VS</div>
                </div>

                {/* Team 2 */}
                <div className={styles.liveTeam}>
                  <div className={styles.liveTeamHeader}>
                    <div className={styles.liveTeamName}>
                      <h3>{currentMatch.awayTeam?.name || 'TBD'}</h3>
                      <div className={styles.liveTeamClub}>
                        {currentMatch.awayTeam?.club_name || ''}
                      </div>
                    </div>
                    {currentMatch.hasScoreData && currentMatch.target !== "N/A" && (
                      <div className={styles.liveTeamScore}>
                        <span className={styles.liveTeamRuns}>
                          Target: {currentMatch.target}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Target Details if available */}
                  {currentMatch.hasScoreData && currentMatch.target !== "N/A" && (
                    <div className={styles.liveScoreDetails}>
                      <div className={styles.liveInningsInfo}>
                        <div className={styles.liveInningsItem}>
                          <span className={styles.liveInningsLabel}>Required Run Rate</span>
                          <span className={`${styles.liveInningsValue} ${styles.highlight}`}>
                            {currentMatch.requiredRunRate}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Navigation Button */}
            {liveMatches.length > 1 && (
              <button
                className={`${styles.liveScoreNav} ${styles.next}`}
                onClick={nextLiveMatch}
                aria-label="Next live match"
              >
                <FaChevronRight />
              </button>
            )}
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className={styles.matchesPage}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading...</p>
        </div>
        <MatchesAPI
          key={`matches-${selectedYear}-${apiKey}`}
          siteId={SITE_ID}
          season={selectedYear}
          onDataLoaded={handleMatchesData}
          onError={handleAPIError}
          onLoading={handleAPILoading}
        />
      </div>
    );
  }




  return (
    <div className={styles.matchesPage}>
      {/* API Component - Loads data with key that changes on season change */}
      <MatchesAPI
        key={`matches-${selectedYear}-${apiKey}`}
        siteId={SITE_ID}
        season={selectedYear}
        onDataLoaded={handleMatchesData}
        onError={handleAPIError}
        onLoading={handleAPILoading}
      />

      <section className={styles.heroSection}>
        <div className={styles.container}>
          <button
            onClick={() => navigate(-1)}
            className={styles.backButton}
          >
            ← Back
          </button>
          <div className={styles.heroContent}>
            <div className={styles.heroBadge}>Cricket Matches</div>
            <h1 className={styles.heroTitle}>
              <span style={{ color: "white" }}>
                {matches.length > 0 ? matches[0].league.name : "League Matches"}
              </span>
            </h1>
            <p className={styles.heroSubtitle} style={{ color: "white" }}>
              All matches from {selectedYear} season. Follow live scores, results and fixtures.
            </p>
          </div>
        </div>
      </section>
      {liveMatches.length > 0 && <LiveScoreSection />}

      {/* Controls Section - Season Selector and Filters */}
      <section className={styles.controlsSection}>
        <div className={styles.container}>
          <div className={styles.controlsGrid}>
            <div style={{ display: "flex", gap: "10px" }}>
              {/* Season Selector - LeagueList style */}
              <div className={styles.seasonSelectorContainer}>
                <div className={styles.seasonSelector}>
                  <label htmlFor="seasonSelect" className={styles.seasonLabel}>
                    <FaFilter />
                    Season:
                  </label>
                  <div className={styles.selectWrapper}>
                    <CustomDropdown
                      options={yearOptions}
                      value={selectedYear}
                      onChange={handleYearChange}
                      placeholder="Select Season"
                    />

                  </div>
                </div>
              </div>
              {/* Competition Filter */}
              {competitions.length > 0 && (
                <div className={styles.competitionFilter}>
                  <div className={styles.selectGroup}>
                    <label htmlFor="competitionSelect">Filter by Competition:</label>
                    <CustomDropdown
                      options={competitionOptions}
                      value={selectedCompetitionId}
                      onChange={(value) => setSelectedCompetitionId(value)}
                    />

                  </div>
                </div>
              )}
            </div>
            {/* Filter Tabs */}
            <div className={styles.filterTabs}>
              {[
                { key: "all", label: "All", count: matchStats.total, color: "#607d8b" },
                { key: "completed", label: "Completed", count: matchStats.completed, color: "#00c853" },
                { key: "upcoming", label: "Upcoming", count: matchStats.upcoming, color: "#FFD700" },
                { key: "live", label: "Live", count: matchStats.live, color: "#546e7a" },

              ]
                .filter(tab => tab.key !== "cancelled" || matchStats.cancelled > 0)
                .map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.filterTab} ${filter === tab.key ? styles.active : ""
                      }`}
                    onClick={() => setFilter(tab.key)}
                    style={{ "--tab-color": tab.color }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
            </div>



          </div>
        </div>
      </section>

      {/* Live Score Section - Only rendered if there are live matches */}

      {/* Matches Section */}
      <section id="matches" className={styles.matchesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {selectedCompetition?.name || "All Competitions"} - {selectedYear} Season
            </h2>
            <div className={styles.resultsCount}>
              Showing {Object.values(groupedMatches).flat().length} match{Object.values(groupedMatches).flat().length !== 1 ? 'es' : ''}
            </div>
          </div>

          {/* Render grouped matches */}
          {renderGroupedMatches()}
        </div>
      </section>
      <Subscription />
    </div>
  );
};

export default Matches;